import {
  Component,
  Input,
  OnInit,
  Output,
  EventEmitter,
  inject,
  DestroyRef,
  ChangeDetectionStrategy,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  FormArray,
  FormBuilder,
  FormGroup,
  Validators,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { Store, select } from '@ngrx/store';

import {
  Dashboard,
  DataObject,
  DashboardPage,
} from 'src/app/core/models/core.models';
import type { WidgetMoveAction } from '../move-copy-widget-dialog.component';

import { SameDashboardOperation } from 'src/app/core/models/widget-operations';

import { AppState } from 'src/app/core/state/app.state';
import { selectCurrentObject } from 'src/app/core/state';

import { Observable } from 'rxjs';
import { map, take } from 'rxjs/operators';
import { TranslateService, TranslatePipe } from '@ngx-translate/core';
import { NgpRovingFocusGroup } from 'ng-primitives/roving-focus';
import { NgpRadioGroup } from 'ng-primitives/radio';
import { SuiRadioGroupDirective, SuiRadioComponent } from '@sui/radio';
import { SuiSelectComponent, SuiSelectOptionComponent } from '@sui/select';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';

@Component({
  selector: 'app-move-copy-same-dashboard',
  templateUrl: './move-copy-same-dashboard.component.html',
  styleUrl: './move-copy-same-dashboard.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    FormsModule,
    ReactiveFormsModule,
    NgpRovingFocusGroup,
    NgpRadioGroup,
    SuiRadioGroupDirective,
    SuiRadioComponent,
    SuiSelectComponent,
    SuiSelectOptionComponent,
    FaIconComponent,
    TranslatePipe,
  ],
})
export class MoveCopySameDashboardComponent implements OnInit {
  @Input() widgets: DataObject[] = [];
  @Input() currentObject?: DataObject;
  @Output() formValidityChange = new EventEmitter<boolean>();

  moveCopyForm: FormGroup;
  availablePages: DashboardPage[] = [];
  currentDashboard$: Observable<Dashboard>;
  globalAction: WidgetMoveAction = 'copy';

  private translateService = inject(TranslateService);
  private destroyRef = inject(DestroyRef);

  constructor(
    private formBuilder: FormBuilder,
    private store: Store<AppState>,
  ) {
    this.currentDashboard$ = this.store.pipe(
      select(selectCurrentObject),
      map((obj) => obj as Dashboard),
    );
  }

  ngOnInit(): void {
    this.initializeForm();
    this.loadAvailablePages();

    // sui-select is CVA-only (no native change event) — drive the bulk-apply
    // off the control's value stream instead of the old template (change).
    this.moveCopyForm
      .get('bulkPageId')
      ?.valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.onBulkPageChange());
  }

  private initializeForm(): void {
    this.moveCopyForm = this.formBuilder.group({
      bulkPageId: [''],
      widgets: this.formBuilder.array(
        this.widgets.map((widget) => this.createWidgetFormGroup(widget)),
      ),
    });

    // Track form validity changes
    this.moveCopyForm.statusChanges.subscribe(() => {
      this.emitValidity();
    });

    // Emit initial validity after setup
    setTimeout(() => {
      this.emitValidity();
    });
  }

  private emitValidity(): void {
    this.formValidityChange.emit(
      this.moveCopyForm.valid && this.availablePages.length > 0,
    );
  }

  private createWidgetFormGroup(widget: DataObject): FormGroup {
    return this.formBuilder.group({
      widget_id: [widget.id, Validators.required],
      title: [widget.title],
      action: [this.globalAction, Validators.required],
      target_page_id: ['', [Validators.required, this.notEmptyStringValidator]],
    });
  }

  private notEmptyStringValidator(control: any) {
    return control.value === '' ? { required: true } : null;
  }

  private loadAvailablePages(): void {
    this.currentDashboard$.subscribe((dashboard) => {
      this.setAvailablePages(dashboard, false);
      this.setInitialCurrentPageSelection(dashboard);
    });
  }

  get widgetFormArray(): FormArray {
    return this.moveCopyForm.get('widgets') as FormArray;
  }

  // * set all Widget target Pages
  onBulkPageChange(): void {
    const bulkPageId = this.moveCopyForm.get('bulkPageId')?.value;
    if (bulkPageId && bulkPageId !== '') {
      this.widgetFormArray.controls.forEach((control) => {
        control.patchValue({ target_page_id: bulkPageId });
      });
    }
  }

  onGlobalActionChange(action: WidgetMoveAction): void {
    this.globalAction = action;
    this.widgetFormArray.controls.forEach((control) => {
      control.patchValue({ action });
    });

    // * force all selects to stay empty to prevent auto-selection
    setTimeout(() => {
      this.currentDashboard$
        .pipe(take(1))
        .subscribe((dashboard) => this.setAvailablePages(dashboard, true));
    }, 0);
  }

  getActionDisplayText(action: WidgetMoveAction): string {
    return this.translateService.instant(
      action === 'copy'
        ? 'move-copy-widget-dialog.label.action-copy'
        : 'move-copy-widget-dialog.label.action-move',
    );
  }

  getFormData(): SameDashboardOperation[] | null {
    if (this.moveCopyForm.valid) {
      return this.widgetFormArray.controls.map((control) => ({
        scenario: 'same-dashboard',
        widget_id: control.value.widget_id,
        action: control.value.action,
        target_page_id: control.value.target_page_id,
        source_object_id: this.currentObject!.id,
        source_object_class_id: this.currentObject!.object_class_id,
        target_object_ids: [this.currentObject!.id],
        target_object_class_id: this.currentObject!.object_class_id,
        switch_datasource: false,
      }));
    }
    return null;
  }

  setAvailablePages(dashboard, isActionToggle: boolean = false): void {
    if (dashboard && dashboard.pages) {
      const currentPageId = this.widgets[0]?.['page_id'];

      if (this.globalAction === 'move') {
        this.availablePages = dashboard.pages.filter(
          (page) => page.id !== currentPageId,
        );
      } else {
        this.availablePages = dashboard.pages;
      }

      // Emit validity when pages change
      this.emitValidity();

      const newAvailablePageIds = new Set(this.availablePages.map((p) => p.id));

      // * smart reset logic when action is toggled
      if (isActionToggle) {
        // * reset bulk page selector if current selection is no longer valid
        const currentBulkPageId = this.moveCopyForm.get('bulkPageId')?.value;

        if (
          currentBulkPageId &&
          !newAvailablePageIds.has(Number(currentBulkPageId))
        ) {
          this.moveCopyForm.patchValue({ bulkPageId: '' });
        }

        // * reset individual Widget target pages only if they're no longer valid
        this.widgetFormArray.controls.forEach((control) => {
          const currentTargetPageId = control.get('target_page_id')?.value;
          if (
            currentTargetPageId &&
            !newAvailablePageIds.has(Number(currentTargetPageId))
          ) {
            control.patchValue({ target_page_id: '' });
          }
        });

        // * force empty values to prevent auto-selection after DOM update
        setTimeout(() => {
          const bulkValue = this.moveCopyForm.get('bulkPageId')?.value;
          if (!bulkValue || bulkValue === '') {
            this.moveCopyForm.patchValue(
              { bulkPageId: '' },
              { emitEvent: false },
            );
          }

          this.widgetFormArray.controls.forEach((control) => {
            const targetValue = control.get('target_page_id')?.value;
            if (!targetValue || targetValue === '') {
              control.patchValue({ target_page_id: '' }, { emitEvent: false });
            }
          });
        }, 0);
      }
    }
  }

  private setInitialCurrentPageSelection(dashboard: Dashboard): void {
    if (!dashboard?.pages) return;

    const currentPageId = this.widgets[0]?.['page_id'];
    if (!currentPageId) return;

    if (
      this.globalAction === 'copy' &&
      this.availablePages.some((p) => p.id === currentPageId)
    ) {
      // * set bulk selector to current page
      this.moveCopyForm.patchValue({ bulkPageId: currentPageId.toString() });

      // * set all widget target pages to current page
      this.widgetFormArray.controls.forEach((control) => {
        control.patchValue({ target_page_id: currentPageId.toString() });
      });
    }
  }
}
